// L'espace du responsable technique (D-093) : la logique pure, sans base ni écran.
// Tranches d'âge du projet sportif, années de naissance d'une catégorie, cycles, état des
// séances, présence, évaluations, répartition proposée pour constituer les équipes, niveaux
// de championnat et obligations de diplôme (jamais bloquantes : on signale, on ne refuse rien).

import { addDays, parseLocalDate, localDate } from './schedule';

// --- Tranches d'âge -------------------------------------------------------------------
export const AGE_GROUPS = ['École', 'U11', 'U13', 'U15', 'U18', 'Seniors'] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];

/** La tranche d'âge d'une catégorie FFBB (comme `age_group` en base). */
export function ageGroup(category: string | null | undefined): AgeGroup | null {
  switch (category) {
    case 'U7': case 'U9': return 'École';
    case 'U11': case 'U13': case 'U15': return category;
    case 'U17': case 'U18': return 'U18';
    case 'U20': case 'Seniors': case 'Loisir': return 'Seniors';
    default: return null;
  }
}

/** Années de naissance d'une catégorie pour la saison qui commence en `startYear`
 *  (2026-2027 : U11 = 2016 et 2017). U18 couvre trois années ; seniors : aucune. */
export function birthYears(category: string | null | undefined, startYear: number): number[] {
  const m = /^U(\d{1,2})$/.exec(category ?? '');
  if (!m) return [];
  const n = Number(m[1]);
  const span = n === 18 ? 3 : 2;
  return Array.from({ length: span }, (_, i) => startYear + 1 - n + i);
}

// --- Dates ------------------------------------------------------------------------------
const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** Lundi de la semaine d'une date 'AAAA-MM-JJ'. */
export function mondayOf(date: string): string {
  const d = parseLocalDate(date);
  return addDays(date, -((d.getDay() + 6) % 7));
}

/** « 16 – 22 nov. », « 30 nov. – 6 déc. ». */
export function weekRange(monday: string): string {
  const a = parseLocalDate(monday), b = parseLocalDate(addDays(monday, 6));
  return a.getMonth() === b.getMonth() ? `${a.getDate()} – ${b.getDate()} ${MONTHS[b.getMonth()]}` : `${a.getDate()} ${MONTHS[a.getMonth()]} – ${b.getDate()} ${MONTHS[b.getMonth()]}`;
}

/** « 16/11 » : la tête de colonne d'une semaine. */
export const dayMonth = (date: string) => { const d = parseLocalDate(date); return `${d.getDate()}/${d.getMonth() + 1}`; };
export const shortDay = (date: string) => { const d = parseLocalDate(date); return `${d.getDate() === 1 ? '1er' : d.getDate()} ${MONTHS[d.getMonth()]}`; };
export const monthName = (date: string) => MONTHS_LONG[parseLocalDate(date).getMonth()]!;
/** « de novembre », « d’octobre » (élision devant une voyelle). */
export const ofMonth = (date: string) => { const m = monthName(date); return /^[aeiouyh]/i.test(m) ? `d’${m}` : `de ${m}`; };
export const firstOfMonth = (date: string) => `${date.slice(0, 7)}-01`;

/** Le dernier week-end (le samedi et le dimanche) ; le samedi ou le dimanche même : celui-ci. */
export function lastWeekend(today: string): [string, string] {
  const dow = parseLocalDate(today).getDay();   // 0 dimanche … 6 samedi
  const sat = dow === 6 ? today : dow === 0 ? addDays(today, -1) : addDays(today, -(dow + 1));
  return [sat, addDays(sat, 1)];
}

// --- Cycles du projet ---------------------------------------------------------------------
export interface Cycle { id: string; position: number; label: string; starts_on: string; ends_on: string }

export const currentCycle = <C extends Cycle>(cycles: C[], today: string): C | null =>
  cycles.find(c => c.starts_on <= today && today <= c.ends_on) ?? null;

/** « semaine 3 sur 9 » du cycle en cours. */
export function cycleWeek(c: Cycle, today: string): { week: number; of: number } {
  const days = (a: string, b: string) => Math.round((parseLocalDate(b).getTime() - parseLocalDate(a).getTime()) / 86_400_000);
  return { week: Math.min(Math.floor(days(c.starts_on, today) / 7) + 1, Math.ceil((days(c.starts_on, c.ends_on) + 1) / 7)), of: Math.ceil((days(c.starts_on, c.ends_on) + 1) / 7) };
}

export type CycleState = 'done' | 'now' | 'next';
export const cycleState = (c: Cycle, today: string): CycleState => (c.ends_on < today ? 'done' : c.starts_on <= today ? 'now' : 'next');

// --- Séances -----------------------------------------------------------------------------
/** Faite avec un plan, faite sans plan, annulée, à venir (« rien » : pas de séance cette semaine). */
export type SessionState = 'plan' | 'noplan' | 'cancelled' | 'upcoming';
export interface SessionRow {
  team_id: string; event_id: string; occ_date: string; starts_at: string; state: SessionState; items: number; minutes: number;
  objective_ids: string[]; theme_ids: string[]; exercise_ids: string[]; comments: number;
}
const ORDER: SessionState[] = ['plan', 'noplan', 'cancelled', 'upcoming'];
export const byState = (a: SessionState, b: SessionState) => ORDER.indexOf(a) - ORDER.indexOf(b);

export interface WeekCount { plan: number; noplan: number; cancelled: number; upcoming: number; done: number; total: number }
export function countStates(rows: Pick<SessionRow, 'state'>[]): WeekCount {
  const c = { plan: 0, noplan: 0, cancelled: 0, upcoming: 0 };
  for (const r of rows) c[r.state]++;
  return { ...c, done: c.plan + c.noplan, total: rows.length };
}

/** Grille des semaines : pour chaque équipe et chaque lundi, les états des séances (dans l'ordre). Les
 *  semaines passées où aucune équipe n'a fait de séance (vacances) sont repliées. */
export function sessionGrid(rows: SessionRow[], teamIds: string[], mondays: string[], today: string) {
  const weekOf = new Map<string, Map<string, SessionState[]>>();
  for (const r of [...rows].sort((a, b) => a.starts_at.localeCompare(b.starts_at))) {
    const m = mondayOf(r.occ_date);
    if (!weekOf.has(r.team_id)) weekOf.set(r.team_id, new Map());
    const w = weekOf.get(r.team_id)!;
    w.set(m, [...(w.get(m) ?? []), r.state]);
  }
  const folded = new Set(mondays.filter(m => addDays(m, 6) < today && !rows.some(r => mondayOf(r.occ_date) === m && (r.state === 'plan' || r.state === 'noplan'))));
  return {
    weeks: mondays.filter(m => !folded.has(m)),
    folded: [...folded],
    cells: (teamId: string, monday: string) => weekOf.get(teamId)?.get(monday) ?? [],
    teamIds,
  };
}

/** Les objectifs du cycle travaillés par une équipe (séances faites pendant le cycle). Une séance
 *  travaille un objectif si elle le reprend, ou si elle contient un de ses exercices. */
export function objectivesWorked(rows: SessionRow[], teamId: string, objectives: { id: string; exercise_ids: string[] }[], from: string, to: string): Map<string, number> {
  const out = new Map(objectives.map(o => [o.id, 0]));
  for (const r of rows) {
    if (r.team_id !== teamId || (r.state !== 'plan' && r.state !== 'noplan') || r.occ_date < from || r.occ_date > to) continue;
    for (const o of objectives) if (r.objective_ids.includes(o.id) || o.exercise_ids.some(x => r.exercise_ids.includes(x))) out.set(o.id, out.get(o.id)! + 1);
  }
  return out;
}

/** Les séances qui ont repris le thème : ses exercices ajoutés depuis le thème, ou ses exercices, pendant son mois. */
export function themeUptake(rows: SessionRow[], theme: { id: string; month: string; exercise_ids: string[] }): Map<string, SessionRow[]> {
  const end = addDays(localDate(new Date(parseLocalDate(theme.month).getFullYear(), parseLocalDate(theme.month).getMonth() + 1, 1)), -1);
  const out = new Map<string, SessionRow[]>();
  for (const r of rows) {
    if (r.state !== 'plan' && r.state !== 'noplan') continue;
    const used = r.theme_ids.includes(theme.id) || (r.occ_date >= theme.month && r.occ_date <= end && theme.exercise_ids.some(x => r.exercise_ids.includes(x)));
    if (used) out.set(r.team_id, [...(out.get(r.team_id) ?? []), r]);
  }
  return out;
}

// --- Présence ----------------------------------------------------------------------------
export const pct = (present: number, total: number): number | null => (total > 0 ? Math.round((100 * present) / total) : null);
/** Couleur de la présence (maquettes) : 70 % et plus, 60 à 69 %, moins de 60 %. */
export const presenceClass = (p: number | null) => (p === null ? '' : p < 60 ? 'p-ko' : p < 70 ? 'p-w' : 'p-ok');
export const PRESENCE_ALERT = 70;

export function sumPresence(rows: { total: number; present: number }[]): { total: number; present: number; pct: number | null } {
  const total = rows.reduce((n, r) => n + r.total, 0), present = rows.reduce((n, r) => n + r.present, 0);
  return { total, present, pct: pct(present, total) };
}

// --- Évaluations -------------------------------------------------------------------------
export const SCALE: [number, string][] = [[1, 'Découverte'], [2, 'En cours'], [3, 'Acquis'], [4, 'Maîtrisé']];
export const MAX_CRITERIA = 5;

export function average(levels: number[]): number | null {
  return levels.length ? Math.round((levels.reduce((a, b) => a + b, 0) / levels.length) * 10) / 10 : null;
}
/** « 2,6 » */
export const fr1 = (n: number | null | undefined) => (n === null || n === undefined ? '–' : n.toFixed(1).replace('.', ','));

/** Toucher une case : – → 1 → 2 → 3 → 4 → 1… */
export const nextLevel = (level: number | null | undefined) => (!level || level >= 4 ? 1 : level + 1);

export type CampaignState = 'prevue' | 'en_cours' | 'finie';
export const campaignState = (c: { starts_on: string; ends_on: string }, today: string): CampaignState =>
  today < c.starts_on ? 'prevue' : today > c.ends_on ? 'finie' : 'en_cours';

/** Où en est une équipe : joueurs notés sur tous les critères. */
export function teamEvalProgress(memberIds: string[], criteriaIds: string[], rated: { person_id: string; criterion_id: string }[]) {
  const per = new Map<string, Set<string>>();
  for (const r of rated) if (criteriaIds.includes(r.criterion_id)) { if (!per.has(r.person_id)) per.set(r.person_id, new Set()); per.get(r.person_id)!.add(r.criterion_id); }
  const done = memberIds.filter(id => criteriaIds.length > 0 && (per.get(id)?.size ?? 0) >= criteriaIds.length).length;
  const started = memberIds.filter(id => (per.get(id)?.size ?? 0) > 0).length;
  return { done, started, total: memberIds.length };
}

export type TeamEvalState = 'fini' | 'en_cours' | 'a_relancer' | 'pas_commence';
/** Fini ; pas commencé ; à relancer (moins de la moitié à moins d'une semaine de la fin) ; en cours. */
export function teamEvalState(p: { done: number; total: number }, daysLeft: number): TeamEvalState {
  if (p.total > 0 && p.done >= p.total) return 'fini';
  if (p.done === 0) return 'pas_commence';
  return daysLeft <= 7 && p.done * 2 < p.total ? 'a_relancer' : 'en_cours';
}
export const TEAM_EVAL_LABEL: Record<TeamEvalState, string> = { fini: 'fini', en_cours: 'en cours', a_relancer: 'à relancer', pas_commence: 'pas commencé' };

// --- Constitution des équipes ----------------------------------------------------------
export interface PoolEntry { id: string; score: number | null; presence: number | null; birthYear: number | null }

/** « Proposer une répartition » : du plus avancé au moins avancé (évaluation, puis présence, puis le
 *  plus âgé) ; la première équipe (le niveau le plus haut) prend sa part, les suivantes le reste. Les
 *  nouveaux sans repère vont à la dernière. Rend l'indice de l'équipe de chaque joueur. */
export function proposeSplit(pool: PoolEntry[], targets: number): Map<string, number> {
  const out = new Map<string, number>();
  if (targets < 1) return out;
  const known = pool.filter(p => p.score !== null || p.presence !== null);
  const unknown = pool.filter(p => p.score === null && p.presence === null);
  known.sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || (b.presence ?? -1) - (a.presence ?? -1) || (a.birthYear ?? 9999) - (b.birthYear ?? 9999));
  const n = pool.length, base = Math.floor(n / targets);
  // Tailles : la première équipe prend la part ronde, le surplus va aux suivantes.
  const sizes = Array.from({ length: targets }, (_, i) => base + (i >= targets - (n % targets) ? 1 : 0));
  const order = [...known, ...unknown];
  let i = 0;
  sizes.forEach((size, t) => { for (let k = 0; k < size && i < order.length; k++, i++) out.set(order[i]!.id, t); });
  return out;
}

export interface CompoChange { id: string; kind: 'moved' | 'new'; from: number | null; to: number }
/** Les changements : passages d'une équipe à l'autre, nouveaux (sans équipe visée aujourd'hui). */
export function compositionChanges(current: Map<string, number | null>, assignment: Map<string, number>): CompoChange[] {
  const out: CompoChange[] = [];
  for (const [id, to] of assignment) {
    const from = current.get(id) ?? null;
    if (from === null) out.push({ id, kind: 'new', from: null, to });
    else if (from !== to) out.push({ id, kind: 'moved', from, to });
  }
  return out.sort((a, b) => (a.kind === b.kind ? a.to - b.to : a.kind === 'moved' ? -1 : 1));
}

// --- Équipes : badge, niveau --------------------------------------------------------------
/** La pastille d'une équipe : SF1, U18F, U11·2, ÉDB… (le nom court s'il existe). */
export function teamBadge(name: string, short?: string | null): string {
  if (short?.trim()) return short.trim().slice(0, 6);
  const n = name.trim(), low = n.toLowerCase();
  const num = /\b(\d)\s*$/.exec(n)?.[1] ?? '';
  const g = /fille|féminin|feminin|dames/.test(low) ? 'F' : /garçon|garcon|masculin|gars/.test(low) ? 'G' : '';
  const u = /^U\s?(\d{1,2})/i.exec(n);
  if (u) return `U${u[1]}${/mixte/.test(low) ? (num ? `·${num}` : '') : `${g}${num}`}`;
  if (/^s[ée]niors?/i.test(n)) return `S${g}${num}`;
  if (/^[ée]cole/i.test(n)) return 'ÉDB';
  return n.split(/\s+/).filter(w => w.length > 2).map(w => w[0]!.toUpperCase()).join('').slice(0, 4) || n.slice(0, 3).toUpperCase();
}

/** Le niveau lu dans le nom d'une compétition FFBB : « Régionale féminine 2 » → « Régionale 2 ». */
export function levelFromCompetition(name: string | null | undefined): string | null {
  const s = (name ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const num = /\b(\d)\b/.exec(s.replace(/U\s?\d{2}/gi, ''))?.[1];
  const tail = num ? ` ${num}` : '';
  if (/pr[ée][- ]?nationale/i.test(s)) return 'Pré-nationale';
  const nf = /\bN([FM])\s?(\d)\b/i.exec(s);
  if (nf) return `N${nf[1]!.toUpperCase()}${nf[2]}`;
  if (/championnat de france|\bnationale\b|[ée]lite/i.test(s)) return `Nationale${tail}`;
  if (/r[ée]gional/i.test(s)) return `Régionale${tail}`;
  if (/d[ée]partemental/i.test(s)) return `Départementale${tail}`;
  return null;
}

export type LevelKey = 'national' | 'prenational' | 'regional_seniors' | 'regional_jeunes' | 'departemental' | 'minibasket';
export const LEVEL_LABEL: Record<LevelKey, string> = {
  national: 'Championnat de France', prenational: 'Pré-nationale', regional_seniors: 'Régionale seniors',
  regional_jeunes: 'Régionale jeunes', departemental: 'Départementale', minibasket: 'Mini-basket',
};
export const LEVEL_HINT: Record<LevelKey, string> = {
  national: 'NF, NM', prenational: 'Seniors', regional_seniors: 'Seniors', regional_jeunes: 'U15 à U20', departemental: 'Seniors et jeunes', minibasket: 'U7 à U11',
};
export const LEVEL_ORDER: LevelKey[] = ['national', 'prenational', 'regional_seniors', 'regional_jeunes', 'departemental', 'minibasket'];

/** La ligne d'obligation qui s'applique à une équipe (niveau et catégorie). */
export function levelKey(level: string | null | undefined, category: string | null | undefined): LevelKey | null {
  if (['U7', 'U9', 'U11'].includes(category ?? '')) return 'minibasket';
  const s = level ?? '';
  if (!s.trim()) return null;
  if (/pr[ée][- ]?nationale/i.test(s)) return 'prenational';
  if (/championnat de france|\bnationale\b|\bN[FM]\s?\d|[ée]lite/i.test(s)) return 'national';
  if (/r[ée]gional/i.test(s)) return ['Seniors', 'Loisir', null, undefined, ''].includes(category) ? 'regional_seniors' : 'regional_jeunes';
  if (/d[ée]partemental/i.test(s)) return 'departemental';
  return null;
}

// --- Diplômes ------------------------------------------------------------------------------
export type DiplomaKey = 'bf_enfants' | 'bf_jeunes' | 'bf_adultes' | 'cqp' | 'de_jeps' | 'des_jeps' | 'autre';
export type DiplomaStatus = 'obtenu' | 'en_formation' | 'inscrit';
export const DIPLOMA_LABEL: Record<DiplomaKey, string> = {
  bf_enfants: 'BF Enfants', bf_jeunes: 'BF Jeunes', bf_adultes: 'BF Adultes', cqp: 'CQP', de_jeps: 'DE JEPS', des_jeps: 'DES JEPS', autre: 'Autre formation',
};
export const DIPLOMA_ORDER: DiplomaKey[] = ['bf_enfants', 'bf_jeunes', 'bf_adultes', 'cqp', 'de_jeps', 'des_jeps', 'autre'];
const RANK: Record<DiplomaKey, number> = { bf_enfants: 1, bf_jeunes: 2, bf_adultes: 3, cqp: 4, de_jeps: 5, des_jeps: 6, autre: 0 };
export const DIPLOMA_STATUS_LABEL: Record<DiplomaStatus, string> = { obtenu: 'obtenu', en_formation: 'en formation', inscrit: 'inscrit' };

/** « BF Jeunes ou plus » ; le plus bas (BF Enfants) se dit seul. */
export const minimumLabel = (d: DiplomaKey) => (d === 'bf_enfants' ? DIPLOMA_LABEL[d] : `${DIPLOMA_LABEL[d]} ou plus`);

export interface Diploma { diploma: DiplomaKey; status: DiplomaStatus }
export interface Rule { level_key: LevelKey; minimum: DiplomaKey; mandatory: boolean }

/** Un coach remplit le minimum s'il a OBTENU un diplôme de ce rang ou au-dessus. */
export const meets = (ds: Diploma[], minimum: DiplomaKey) => ds.some(d => d.status === 'obtenu' && d.diploma !== 'autre' && RANK[d.diploma] >= RANK[minimum]);
/** En route : inscrit ou en formation pour un diplôme suffisant. */
export const onTheWay = (ds: Diploma[], minimum: DiplomaKey) => ds.some(d => d.status !== 'obtenu' && d.diploma !== 'autre' && RANK[d.diploma] >= RANK[minimum]);

export type Compliance = 'ok' | 'regulariser' | 'conseille' | 'na';
/** Conformité d'une équipe à l'obligation de son niveau : jamais bloquante, seulement signalée. Le coach
 *  principal compte ; un adjoint aussi si le club l'a réglé ainsi (« si ta ligue l'accepte »). */
export function compliance(rule: Rule | null, heads: Diploma[][], assistants: Diploma[][], assistantCounts: boolean): Compliance {
  if (!rule) return 'na';
  const who = assistantCounts ? [...heads, ...assistants] : heads;
  if (who.some(ds => meets(ds, rule.minimum))) return 'ok';
  return rule.mandatory ? 'regulariser' : 'conseille';
}

/** Le diplôme le plus haut obtenu (pour une ligne « Nadia a le BF Enfants »). */
export function bestDiploma(ds: Diploma[]): DiplomaKey | null {
  const got = ds.filter(d => d.status === 'obtenu' && d.diploma !== 'autre').sort((a, b) => RANK[b.diploma] - RANK[a.diploma]);
  return got[0]?.diploma ?? null;
}

// --- Thème du mois ---------------------------------------------------------------------------
export const totalOf = (items: { minutes: number | null }[]) => items.reduce((n, i) => n + (i.minutes ?? 0), 0);

/** « U11 à seniors », « U13 et U15 », « toutes les équipes ». */
export function groupsLabel(groups: string[]): string {
  const g = AGE_GROUPS.filter(x => groups.includes(x));
  if (!g.length) return 'aucune équipe';
  if (g.length === AGE_GROUPS.length) return 'toutes les équipes';
  const idx = g.map(x => AGE_GROUPS.indexOf(x));
  const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1);
  const low = (x: string) => (x === 'Seniors' ? 'seniors' : x === 'École' ? 'l’école de basket' : x);
  if (g.length >= 3 && contiguous) return `${g[0] === 'École' ? 'L’école de basket' : g[0]} à ${low(g[g.length - 1]!)}`;
  return g.length === 1 ? low(g[0]!) : `${g.slice(0, -1).map(low).join(', ')} et ${low(g[g.length - 1]!)}`;
}

/** Liste française : « Thomas, Benoît et Kevin ». */
export const andList = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`);
